import React, { useEffect, useState } from "react"
import { motion } from 'framer-motion'
import API from "../../Common/API"
import { useDispatch } from "react-redux"
import { Dropzone, FileMosaic } from "@files-ui/react";
import { setAlert } from "../../Store/Theme/actions"
import Button from "../../Components/Button";
import { useHistory } from "react-router-dom";

const ManageImages = ({ match, userId: userIdProp, onClose, onSaved }) => {

    const dispatch = useDispatch()
    const [images, setImages] = useState([])
    const [loading, setLoading] = useState(true)
    const [files, setFiles] = React.useState([]);
    const [count, setCount] = useState(0)
    const [buttonDisable, setButtonDisable] = useState(false)
    const history = useHistory()

    const resolvedUserId = userIdProp || match?.params?.id

    useEffect(() => {
        if (!resolvedUserId) return
        setLoading(true)
        API.get(`/user/getImagesById/${resolvedUserId}`).then((imageRes) => {
            if (imageRes.status === 200) {
                setImages(imageRes.data.data.map((c) => {
                    c.removed = false
                    return c
                }))
            }
            setLoading(false)
        }).catch((err) => {
            setLoading(false)
            dispatch(setAlert({ title: 'Error', subtitle: 'Something went wrong', active: true, type: 'error' }))
        })
    }, [count, resolvedUserId])

    const updateFiles = (incomingFiles) => {
        setFiles(incomingFiles);
    };

    const removeFile = (id) => {
        setFiles((prev) => prev.filter((x) => x.id !== id));
    };

    const removeImage = (value) => {
        const updatedImages = images.map((c) => {
            if (c._id === value._id) {
                return { ...c, removed: !c.removed };
            }
            return c;
        });
        setImages(updatedImages);
    }

    const fileToBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve({ imageUrl: reader.result, _id: `img_${Date.now()}_${Math.random().toString(36).slice(2)}` })
        reader.onerror = reject
        reader.readAsDataURL(file)
    })

    const uploadImages = async () => {
        setButtonDisable(true)
        try {
            // Keep existing images not marked for removal
            const keptImages = images
                .filter((c) => !c.removed)
                .map((c) => ({ imageUrl: c.imageUrl, _id: c._id }))

            // Convert new files to base64
            const newBase64 = await Promise.all(files.map((f) => fileToBase64(f.file)))

            const allImages = [...keptImages, ...newBase64].slice(0, 5)

            const res = await API.post('/user/editUserByAdmin', { _id: resolvedUserId, images: allImages })
            if (res.status === 200) {
                dispatch(setAlert({ title: 'Success', subtitle: 'Images saved successfully', active: true, type: 'success' }))
                setCount((c) => c + 1)
                setFiles([])
                if (typeof onSaved === 'function') onSaved(allImages)
            }
        } catch (err) {
            dispatch(setAlert({ title: 'Error', subtitle: 'Something went wrong', active: true, type: 'error' }))
        } finally {
            setButtonDisable(false)
        }
    }

    return (
        <>
            {
                loading ? (
                    <div className="flex items-center justify-center py-10 text-sm text-tpColor">Loading images…</div>
                ) : (
                    <motion.div className="w-full overflow-hidden px-4 py-4 flex flex-col gap-4">
                        {images && images.length > 0 && (
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                                {images.map((c, index) => (
                                    <div key={index} className="relative overflow-hidden rounded-2xl border border-borderColor bg-lightGrey/30">
                                        <img
                                            src={c.imageUrl}
                                            alt={`Image ${index + 1}`}
                                            className={`h-36 w-full object-cover ${c.removed ? 'opacity-40 grayscale' : ''}`}
                                        />
                                        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between bg-black/40 px-3 py-2">
                                            <span className="text-xs font-semibold text-white">Photo {index + 1}</span>
                                            <button
                                                type="button"
                                                className={`rounded-full px-3 py-1 text-xs font-bold ${c.removed ? 'bg-primeColor text-white' : 'bg-red-600 text-white'}`}
                                                onClick={() => removeImage(c)}
                                            >
                                                {c.removed ? 'Retain' : 'Remove'}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {images.filter((x) => !x.removed).length < 5 && (
                            <Dropzone
                                className="relative"
                                onChange={updateFiles}
                                value={files}
                                accept="image/*"
                                maxFiles={5 - images.filter((x) => !x.removed).length}
                            >
                                {files.map((file) => (
                                    <FileMosaic preview={true} key={file.id} {...file} onDelete={removeFile} info />
                                ))}
                            </Dropzone>
                        )}

                        <p className="text-center text-xs text-tpColor">
                            {images.filter((x) => !x.removed).length + files.length} / 5 photos · max 5 photos allowed
                        </p>

                        <div className="flex justify-center gap-3 pt-2">
                            <Button type="secondary" injectClass="rounded-full px-5" onClick={() => { if (typeof onClose === 'function') onClose(); else history.goBack(); }}>Cancel</Button>
                            <Button type="primary" injectClass="rounded-full px-5" disabled={buttonDisable} onClick={() => !buttonDisable && uploadImages()}>
                                {buttonDisable ? 'Saving…' : 'Save Photos'}
                            </Button>
                        </div>
                    </motion.div>
                )
            }
        </>
    )
}

export default ManageImages